import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Check,
  Wand2,
  Copy,
  ChevronDown,
  Tag as TagIconLucide,
  AlertCircle,
  X,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { WizardStepIndicator, WizardStepDef } from "@/components/ui/wizard-step-indicator";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Info, ExternalLink, Shield } from "lucide-react";
import {
  PppcTargetStep,
  PppcTargetValue,
  validatePppcTarget,
} from "./PppcTargetStep";
import {
  PrivacyPermissionsForm,
  PrivacyPermissionsFormValue,
  emptyPppcFormValue,
} from "./PrivacyPermissionsForm";
import { PPPC_SERVICE_GROUPS, PppcChoicesMap, PppcServiceKey } from "@/types/pppc";
import { useSavePppcPolicy, usePppcPolicyTags } from "@/hooks/usePppcPolicy";
import { useAvailableTags } from "@/hooks/useAppConfigurations";
import { TagIcon } from "@/components/tags/TagIcon";
import { WIZARD_VALIDATION_MESSAGES } from "@/services/wizardValidation";
import { recommendTagsForApp } from "@/services/tagRecommendations";
import { useTagsDeviceCount } from "@/hooks/useTagsDeviceCount";
import { DeploymentSummaryPanel } from "@/components/applications/shared/DeploymentSummaryPanel";
import { ScopeAndAssignStep } from "@/components/shared/ScopeAndAssignStep";
import { toast } from "sonner";

type StepId = "target" | "code" | "services" | "assignment";

const STEPS: WizardStepDef[] = [
  { id: "target", name: "Target App", description: "Pick or describe the app" },
  { id: "code", name: "Code Requirement", description: "Code signing requirement" },
  { id: "services", name: "Services", description: "Allow / deny privacy services" },
  { id: "assignment", name: "Assignment", description: "Deployment tags" },
];

interface PppcPolicyWizardProps {
  /** When provided, the wizard is locked to this app (no Step 1 mode toggle). */
  lockedTarget?: {
    appName: string;
    bundleId: string;
  };
  /** Initial values for edit mode. */
  initial?: {
    target?: Partial<PppcTargetValue>;
    permissions?: PrivacyPermissionsFormValue;
    existingPolicyId?: string;
    tagIds?: string[];
  };
  /** Where to go after save / cancel. */
  returnTo?: string;
}

const CODE_REQ_MIN = 10;

/**
 * Build a starter Code Requirement from a Bundle ID.
 * This produces the standard "anchor apple generic + identifier" template that
 * works for most Developer ID-signed apps. Users typically still need to paste
 * the team's leaf certificate hash from `codesign -dr -`, but this gets them
 * 90% of the way there and is a valid syntactic requirement string.
 */
const generateCodeRequirementFromBundleId = (bundleId: string): string => {
  const id = bundleId.trim();
  if (!id) return "";
  return `identifier "${id}" and anchor apple generic and certificate 1[field.1.2.840.113635.100.6.2.6] /* exists */ and certificate leaf[field.1.2.840.113635.100.6.1.13] /* exists */`;
};

export const PppcPolicyWizard = ({
  lockedTarget,
  initial,
  returnTo = "/configurations/pppc",
}: PppcPolicyWizardProps) => {
  const navigate = useNavigate();
  const save = useSavePppcPolicy(undefined);

  const lockedSteps = !!lockedTarget;

  const [stepId, setStepId] = useState<StepId>(
    lockedSteps ? "code" : "target",
  );
  const [completed, setCompleted] = useState<StepId[]>([]);

  const [target, setTarget] = useState<PppcTargetValue>(() => {
    if (lockedTarget) {
      return {
        mode: "catalog",
        catalogAppId: undefined,
        appName: lockedTarget.appName,
        identifier: lockedTarget.bundleId,
        identifierType: "bundleID",
      };
    }
    return {
      mode: initial?.target?.mode ?? "catalog",
      catalogAppId: initial?.target?.catalogAppId,
      appName: initial?.target?.appName ?? "",
      identifier: initial?.target?.identifier ?? "",
      identifierType: initial?.target?.identifierType ?? "bundleID",
    };
  });

  const [permissions, setPermissions] = useState<PrivacyPermissionsFormValue>(
    initial?.permissions ?? emptyPppcFormValue(),
  );

  const [tagIds, setTagIds] = useState<string[]>(
    (initial?.tagIds ?? []).slice().sort(),
  );
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [allCustomers, setAllCustomers] = useState(false);
  const [tagsErr, setTagsErr] = useState<string | undefined>();
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const tagsCardRef = useRef<HTMLDivElement | null>(null);

  // External targets get a real Assignment step; catalog targets inherit
  // deployment from the app and skip Assignment entirely.
  const showAssignmentStep = !lockedTarget && target.mode === "external";
  const visibleSteps = useMemo(() => {
    let steps = STEPS;
    if (lockedSteps) steps = steps.filter((s) => s.id !== "target");
    if (!showAssignmentStep) steps = steps.filter((s) => s.id !== "assignment");
    return steps;
  }, [lockedSteps, showAssignmentStep]);

  // If `target.mode` flips while sitting on the assignment step, pop back.
  useEffect(() => {
    if (!visibleSteps.find((s) => s.id === stepId)) {
      setStepId(visibleSteps[visibleSteps.length - 1].id as StepId);
    }
  }, [visibleSteps, stepId]);

  const tagsQuery = useAvailableTags();
  const availableTags = tagsQuery.data ?? [];
  const selectedTags = availableTags.filter((t) => tagIds.includes(t.id));
  const tagsDeviceCount = useTagsDeviceCount(tagIds);
  const recommendedTags = useMemo(
    () =>
      recommendTagsForApp(availableTags, target.appName).filter(
        (t) => !tagIds.includes(t.id),
      ),
    [availableTags, target.appName, tagIds],
  );

  // Hydrate the tag baseline once the existing policy's tags resolve (edit mode).
  const existingTagsQuery = usePppcPolicyTags(initial?.existingPolicyId);
  const [hydratedTags, setHydratedTags] = useState(false);
  useEffect(() => {
    if (
      initial?.existingPolicyId &&
      !hydratedTags &&
      existingTagsQuery.data
    ) {
      setTagIds(existingTagsQuery.data.map((t) => t.id).sort());
      setHydratedTags(true);
    }
  }, [initial?.existingPolicyId, existingTagsQuery.data, hydratedTags]);

  const toggleTag = (tagId: string) => {
    setTagIds((prev) => {
      const has = prev.includes(tagId);
      const next = has ? prev.filter((id) => id !== tagId) : [...prev, tagId];
      return next.sort();
    });
    if (tagsErr) setTagsErr(undefined);
  };

  const removeTag = (tagId: string) => {
    setTagIds((prev) => prev.filter((id) => id !== tagId));
  };

  const [targetErrors, setTargetErrors] = useState<ReturnType<typeof validatePppcTarget>>({});
  const [codeErr, setCodeErr] = useState<string | undefined>();
  const [servicesErr, setServicesErr] = useState<string | undefined>();
  const [serviceIssues, setServiceIssues] = useState<
    import("@/services/pppcValidation").PppcValidationIssue[]
  >([]);

  const [submitting, setSubmitting] = useState<"draft" | "publish" | null>(null);

  const stepIndex = visibleSteps.findIndex((s) => s.id === stepId);
  const tagsRequired = !lockedTarget && target.mode === "external";

  const markComplete = (id: StepId) => {
    setCompleted((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const goNext = () => {
    if (stepId === "target") {
      const errs = validatePppcTarget(target);
      setTargetErrors(errs);
      if (Object.keys(errs).length) return;
      markComplete("target");
      setStepId("code");
      return;
    }
    if (stepId === "code") {
      if (permissions.codeRequirement.trim().length < CODE_REQ_MIN) {
        setCodeErr(`Code requirement must be at least ${CODE_REQ_MIN} characters.`);
        return;
      }
      setCodeErr(undefined);
      markComplete("code");
      setStepId("services");
      return;
    }
    if (stepId === "services") {
      const hasAny = Object.values(permissions.choices).some(
        (c) => c === "allow" || c === "deny",
      );
      if (!hasAny) {
        setServicesErr("Configure at least one service (Allow or Deny).");
        return;
      }
      setServicesErr(undefined);
      markComplete("services");
      // Catalog targets skip Assignment — the header shows Save buttons
      // instead of Next, so we don't navigate further.
      if (showAssignmentStep) setStepId("assignment");
      return;
    }
  };

  const goBack = () => {
    const idx = visibleSteps.findIndex((s) => s.id === stepId);
    if (idx > 0) setStepId(visibleSteps[idx - 1].id as StepId);
  };

  const handleSave = async (publish: boolean) => {
    // Validate everything except the assignment step.
    const tErr = validatePppcTarget(target);
    setTargetErrors(tErr);
    if (Object.keys(tErr).length) {
      setStepId(lockedSteps ? "code" : "target");
      toast.error("Fix the highlighted fields before saving.");
      return;
    }
    if (permissions.codeRequirement.trim().length < CODE_REQ_MIN) {
      setCodeErr(`Code requirement must be at least ${CODE_REQ_MIN} characters.`);
      setStepId("code");
      return;
    }
    const hasAny = Object.values(permissions.choices).some(
      (c) => c === "allow" || c === "deny",
    );
    if (!hasAny) {
      setServicesErr("Configure at least one service (Allow or Deny).");
      setStepId("services");
      return;
    }
    const issues = (
      await import("@/services/pppcValidation")
    ).validatePppcChoices(permissions.choices);
    setServiceIssues(issues);
    if (issues.length > 0) {
      setStepId("services");
      toast.error(
        issues.length === 1
          ? issues[0].message
          : `${issues.length} invalid custom TCC services. Fix them before saving.`,
      );
      return;
    }

    // Tags are required only for external targets — catalog targets inherit
    // their deployment scope from the app itself.
    if (tagsRequired && tagIds.length === 0) {
      setTagsErr(WIZARD_VALIDATION_MESSAGES.noTags);
      setStepId("assignment");
      toast.error(WIZARD_VALIDATION_MESSAGES.noTags);
      requestAnimationFrame(() => {
        tagsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setTagsErr(undefined);

    setSubmitting(publish ? "publish" : "draft");
    try {
      await save.mutateAsync({
        existingPolicyId: initial?.existingPolicyId,
        appName: target.appName,
        bundleId: target.identifier,
        identifierType: target.identifierType,
        codeRequirement: permissions.codeRequirement,
        choices: permissions.choices,
        tagIds,
        previousTagIds: hydratedTags
          ? (existingTagsQuery.data ?? []).map((t) => t.id).sort()
          : [],
      });
      navigate(returnTo);
    } catch {
      // toast handled by hook
    } finally {
      setSubmitting(null);
    }
  };

  // Live revalidation as user types after first attempt
  useEffect(() => {
    if (codeErr && permissions.codeRequirement.trim().length >= CODE_REQ_MIN) {
      setCodeErr(undefined);
    }
  }, [permissions.codeRequirement, codeErr]);

  const isLastStep = stepId === visibleSteps[visibleSteps.length - 1].id;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-background border-b">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (stepIndex === 0 ? navigate(returnTo) : goBack())}
              disabled={!!submitting}
              aria-label={stepIndex === 0 ? "Exit wizard" : "Go to previous step"}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-base font-semibold leading-tight">
                {initial?.existingPolicyId ? "Edit PPPC Policy" : "New PPPC Policy"}
              </h1>
              <p className="text-xs text-muted-foreground leading-tight">
                Privacy Preferences Policy Control
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isLastStep ? (
              <Button onClick={goNext} disabled={!!submitting}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSave(false)}
                  disabled={!!submitting}
                >
                  {submitting === "draft" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Save as Draft
                </Button>
                <Button onClick={() => handleSave(true)} disabled={!!submitting}>
                  {submitting === "publish" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Publish
                </Button>
              </>
            )}
          </div>
        </div>
        <WizardStepIndicator
          steps={visibleSteps}
          currentStep={stepId}
          completedSteps={completed}
        />
      </div>

      {/* Body */}
      <ScrollArea className="flex-1">
        <div className="px-6 py-8">
          {stepId === "target" && (
            <PppcTargetStep
              value={target}
              onChange={setTarget}
              errors={targetErrors}
              locked={lockedSteps}
              currentChoices={permissions.choices}
              currentCodeRequirement={permissions.codeRequirement}
              onImport={(result) => {
                // The dialog already merged choices using "keep existing, add new"
                // and resolved any code-requirement conflict. We just write the
                // results back into target + permissions state and clear stale
                // service issues so the user can move forward.
                setTarget((prev) => ({
                  ...prev,
                  // Switch to "external" so the imported identifier is editable
                  // and we don't try to look it up in the catalog list.
                  mode: "external",
                  catalogAppId: undefined,
                  appName:
                    prev.appName?.trim() ||
                    result.sourceLabel ||
                    result.identifier,
                  identifier: result.identifier,
                  identifierType: result.identifierType,
                }));
                setPermissions({
                  codeRequirement: result.codeRequirement,
                  choices: result.choices,
                });
                setServiceIssues([]);
                setCodeErr(undefined);
                setServicesErr(undefined);
              }}
            />
          )}

          {stepId === "code" && (
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Code Requirement</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                The code requirement string codesign uses to verify the binary
                matches before applying privacy approvals.
              </p>

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">App</Label>
                      <Input
                        value={target.appName || "—"}
                        readOnly
                        className="mt-1 bg-muted"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        {target.identifierType === "path" ? "Path" : "Bundle ID"}
                      </Label>
                      <Input
                        value={target.identifier || "—"}
                        readOnly
                        className="mt-1 font-mono text-sm bg-muted"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="pppc-code-req" className="text-sm">
                        Code Requirement{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        {target.identifierType === "bundleID" && target.identifier ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              const generated = generateCodeRequirementFromBundleId(
                                target.identifier,
                              );
                              setPermissions({
                                ...permissions,
                                codeRequirement: generated,
                              });
                              toast.success("Code requirement generated", {
                                description:
                                  "Verify the certificate fields match the app's signing team.",
                              });
                            }}
                          >
                            <Wand2 className="h-3 w-3 mr-1" />
                            Generate from Bundle ID
                          </Button>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                              >
                                <Wand2 className="h-3 w-3 mr-1" />
                                How to obtain
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-96 text-xs space-y-3">
                              <div>
                                <p className="font-semibold mb-1">
                                  Get the code requirement from the signed app
                                </p>
                                <p className="text-muted-foreground">
                                  Auto-generation needs a Bundle ID. For a path-based
                                  target, run this on a Mac that has the app installed:
                                </p>
                              </div>
                              <div className="relative">
                                <pre className="bg-muted rounded p-2 pr-8 font-mono text-[11px] whitespace-pre-wrap break-all">
                                  {`codesign -dr - "${target.identifier || "/Applications/YourApp.app"}" 2>&1 | sed -n 's/^designated => //p'`}
                                </pre>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute top-1 right-1 h-6 w-6 p-0"
                                  onClick={() => {
                                    const cmd = `codesign -dr - "${target.identifier || "/Applications/YourApp.app"}" 2>&1 | sed -n 's/^designated => //p'`;
                                    navigator.clipboard.writeText(cmd);
                                    toast.success("Command copied");
                                  }}
                                  aria-label="Copy command"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              <p className="text-muted-foreground">
                                Paste the output (the line after{" "}
                                <code className="font-mono">designated =&gt;</code>)
                                into the field.
                              </p>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                    <Input
                      id="pppc-code-req"
                      value={permissions.codeRequirement}
                      onChange={(e) =>
                        setPermissions({
                          ...permissions,
                          codeRequirement: e.target.value,
                        })
                      }
                      placeholder={`identifier "${target.identifier || "your.app.id"}" and anchor apple generic and ...`}
                      className="mt-1 font-mono text-xs"
                      aria-invalid={!!codeErr}
                    />
                    {codeErr && (
                      <p className="text-xs text-destructive mt-1">{codeErr}</p>
                    )}
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        For best accuracy, run{" "}
                        <code className="px-1 py-0.5 rounded bg-muted font-mono">
                          codesign -dr - /Applications/YourApp.app
                        </code>{" "}
                        and paste the value after{" "}
                        <code className="font-mono">designated =&gt;</code>.{" "}
                        <a
                          href="https://developer.apple.com/documentation/devicemanagement/privacypreferencespolicycontrol"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Apple docs <ExternalLink className="h-3 w-3" />
                        </a>
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {stepId === "services" && (
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Privacy Services</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose Allow or Deny for each service. Leave others as{" "}
                <strong>Not set</strong> to skip them.
              </p>
              {servicesErr && (
                <Alert variant="destructive">
                  <AlertDescription>{servicesErr}</AlertDescription>
                </Alert>
              )}
              <Card>
                <CardContent className="pt-6">
                  <PrivacyPermissionsForm
                    bundleId={target.identifier}
                    value={permissions}
                    onChange={(v) => {
                      setPermissions(v);
                      if (serviceIssues.length > 0) setServiceIssues([]);
                    }}
                    scrollServices={false}
                    errors={{ serviceIssues }}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/*
           * Catalog targets skip the Assignment step entirely (their
           * deployment "follows the app"), so the Deployment Summary panel
           * would otherwise never render. Mount the same panel here in
           * catalog mode with `tagsRequired={false}` so it surfaces the
           * "Follows the app" subtitle and the catalog-flavored info
           * tooltip — matches the App Config wizard's catalog rendering.
           */}
          {stepId === "services" && target.mode !== "external" && (
            <div className="mt-6">
              <DeploymentSummaryPanel
                identity={[
                  { label: "App", value: target.appName ?? "" },
                  {
                    label:
                      target.identifierType === "path" ? "Path" : "Bundle ID",
                    value: target.identifier ?? "",
                    mono: true,
                  },
                ]}
                customerCount={selectedCustomers.length}
                selectedTagNames={selectedTags.map((t) => t.name)}
                deviceCount={tagsDeviceCount}
                payloadCount={countConfigured(permissions.choices)}
                payloadLabel={`Services configured (of ${countTotal()})`}
                platformCount={1}
                tagsRequired={false}
                hideDraftAlert
              />
            </div>
          )}

          {stepId === "assignment" && (
            <div className="space-y-6" ref={tagsCardRef}>
              <div className="flex items-center gap-2 max-w-5xl mx-auto">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Assignment</h2>
              </div>

              <ScopeAndAssignStep
                heading="Scope & Assign"
                subtitle="Select customers to scope the available tags, then pick the tags whose devices should receive this policy."
                hideAllCustomers
                hideSummary
                selectedCustomers={selectedCustomers}
                onCustomersChange={setSelectedCustomers}
                allCustomers={allCustomers}
                onAllCustomersChange={setAllCustomers}
                selectedTags={tagIds}
                onTagsChange={(next) => {
                  setTagIds(next);
                  if (tagsErr && next.length > 0) setTagsErr(undefined);
                }}
                payloadCount={countConfigured(permissions.choices)}
                platformCount={1}
                policyId={initial?.existingPolicyId}
              />

              <DeploymentSummaryPanel
                identity={[
                  { label: "App", value: target.appName ?? "" },
                  {
                    label:
                      target.identifierType === "path" ? "Path" : "Bundle ID",
                    value: target.identifier ?? "",
                    mono: true,
                  },
                ]}
                customerCount={selectedCustomers.length}
                selectedTagNames={selectedTags.map((t) => t.name)}
                deviceCount={tagsDeviceCount}
                payloadCount={countConfigured(permissions.choices)}
                payloadLabel={`Services configured (of ${countTotal()})`}
                platformCount={1}
                hideDraftAlert
              />

              {tagsErr && (
                <Alert variant="destructive" role="alert" className="max-w-5xl mx-auto">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Select at least one tag</AlertTitle>
                  <AlertDescription>
                    {tagsErr}{" "}
                    {WIZARD_VALIDATION_MESSAGES.noTagsExternalDetailed}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Bottom nav: only Back, primary action lives in the sticky header */}
      <div className="border-t bg-background px-6 py-3 flex items-center justify-start">
        <Button
          variant="ghost"
          onClick={() => (stepIndex === 0 ? navigate(returnTo) : goBack())}
          disabled={!!submitting}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </div>
  );
};

const countConfigured = (choices: PppcChoicesMap) =>
  (Object.keys(choices) as PppcServiceKey[]).filter(
    (k) => choices[k] === "allow" || choices[k] === "deny",
  ).length;

const countTotal = () =>
  PPPC_SERVICE_GROUPS.reduce((sum, g) => sum + g.services.length, 0);
